import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  ConnectPipelineRepositoryRequestBody,
  PipelineGitConnectionResponse,
  PipelineGitExportResponse,
  PipelineGitImportApplyRequestBody,
  PipelineGitImportApplyResponse,
  PipelineGitImportCheckResponse,
  PipelineGitImportRequestBody,
} from "common";

/**
 * Whole pipeline git: the pipeline page's Git tab (connect, export) and the Import from Git
 * wizard (check, apply). The shapes are the ones the routes validate, shared through `common`.
 */

export const pipelineGitConnectionKey = (
  pipelineId: number
): (string | number)[] => ["/api/v1/pipeline", pipelineId, "git", "connection"];

/** Check plan statuses that refuse the whole import (the server's blocking set). */
export const BLOCKING_IMPORT_STATUSES = [
  "uuid-conflict",
  "contract-refused",
  "unreadable",
] as const;

export const isBlockingImportStatus = (status: string): boolean =>
  (BLOCKING_IMPORT_STATUSES as readonly string[]).includes(status);

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * The connection GET needs write permission, so a 403 is an answer ("you cannot manage this
 * pipeline's repository"), not a failure to show as a broken panel.
 */
export const isForbidden = (error: unknown): boolean => 403 === statusOf(error);

export const pipelineGitDetailOf = (
  error: ErrorNotification | null | undefined,
  fallback: string
): string => error?.response?.data?.message ?? fallback;

export const usePipelineGitConnection = (
  pipelineId: number | undefined
): UseQueryResult<PipelineGitConnectionResponse, ErrorNotification> =>
  useQuery<PipelineGitConnectionResponse, ErrorNotification>({
    queryKey: pipelineGitConnectionKey(pipelineId ?? 0),
    queryFn: async () => {
      const res = await axios.get(
        `/api/v1/pipeline/${pipelineId}/git/connection`
      );
      return res.data as PipelineGitConnectionResponse;
    },
    enabled: undefined !== pipelineId && pipelineId > 0,
    // A 403 is final; retrying it only delays the message.
    retry: false,
  });

export const useConnectPipelineRepo = (
  pipelineId: number
): UseMutationResult<
  NonNullable<PipelineGitConnectionResponse>,
  ErrorNotification,
  ConnectPipelineRepositoryRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation<
    NonNullable<PipelineGitConnectionResponse>,
    ErrorNotification,
    ConnectPipelineRepositoryRequestBody
  >({
    mutationFn: async body => {
      const res = await axios.post(
        `/api/v1/pipeline/${pipelineId}/git/connect`,
        body
      );
      return res.data as NonNullable<PipelineGitConnectionResponse>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: pipelineGitConnectionKey(pipelineId),
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-git-connect-error",
        message: "Failed to connect repository",
        details: pipelineGitDetailOf(
          error,
          "Check the repository URL, the folder name and the branch."
        ),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useExportPipelineRevision = (
  pipelineId: number
): UseMutationResult<PipelineGitExportResponse, ErrorNotification, void> => {
  const { setNotification } = useNotifications();

  return useMutation<PipelineGitExportResponse, ErrorNotification, void>({
    mutationFn: async () => {
      const res = await axios.post(`/api/v1/pipeline/${pipelineId}/git/export`);
      return res.data as PipelineGitExportResponse;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "pipeline-git-export-error",
        message: "Export failed",
        details: pipelineGitDetailOf(
          error,
          "Check that the repository and its credential are correct."
        ),
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/** A read: names what importing would do, stores nothing, so it invalidates nothing. */
export const usePipelineGitImportCheck = (): UseMutationResult<
  PipelineGitImportCheckResponse,
  ErrorNotification,
  PipelineGitImportRequestBody
> =>
  useMutation<
    PipelineGitImportCheckResponse,
    ErrorNotification,
    PipelineGitImportRequestBody
  >({
    mutationFn: async body => {
      const res = await axios.post("/api/v1/pipeline/git/import/check", body);
      return res.data as PipelineGitImportCheckResponse;
    },
  });

export const usePipelineGitImportApply = (): UseMutationResult<
  PipelineGitImportApplyResponse,
  ErrorNotification,
  PipelineGitImportApplyRequestBody
> => {
  const queryClient = useQueryClient();

  return useMutation<
    PipelineGitImportApplyResponse,
    ErrorNotification,
    PipelineGitImportApplyRequestBody
  >({
    mutationFn: async body => {
      const res = await axios.post("/api/v1/pipeline/git/import", body);
      return res.data as PipelineGitImportApplyResponse;
    },
    onSuccess: async () => {
      // The new pipeline belongs in the list, and its processes in the workflow builder's sidebar.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["pipelineList"] }),
        queryClient.invalidateQueries({
          queryKey: ["/api/project/v1/pipeline-builder-node"],
        }),
      ]);
    },
  });
};
